import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirestoreInstagramReel } from '@core/services/instagram-reels/models/firestore-instagram-reel'
import type {
  InstagramMediaDetectionSource,
  InstagramMediaKind,
  InstagramReel,
} from '@domain/models/instagram-reel'

const VALID_MEDIA_KINDS = new Set<InstagramMediaKind>(['reel', 'post', 'unknown'])
const VALID_MEDIA_DETECTION_SOURCES = new Set<InstagramMediaDetectionSource>([
  'product_type',
  'permalink_reel',
  'permalink_post',
  'fallback',
])

function toNullableString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function toOptionalBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function isFiniteDate(value: Date): boolean {
  return Number.isFinite(value.getTime())
}

function toDateFromEpochMillis(value: number): Date | null {
  if (!Number.isFinite(value)) {
    return null
  }

  const parsedDate = new Date(value)
  return isFiniteDate(parsedDate) ? parsedDate : null
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function toOptionalDate(value: unknown): Date | null {
  if (value instanceof Timestamp) {
    const asDate = value.toDate()
    return isFiniteDate(asDate) ? asDate : null
  }

  if (value instanceof Date) {
    return isFiniteDate(value) ? value : null
  }

  if (typeof value === 'number') {
    return toDateFromEpochMillis(value)
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedDate = new Date(value)
    return isFiniteDate(parsedDate) ? parsedDate : null
  }

  if (value && typeof value === 'object') {
    const maybeToDate = (value as { toDate?: () => unknown }).toDate
    if (typeof maybeToDate === 'function') {
      const asDate = maybeToDate()
      if (asDate instanceof Date && isFiniteDate(asDate)) {
        return asDate
      }
    }

    const seconds = toNumber((value as { seconds?: unknown }).seconds)
    if (seconds !== null) {
      const nanoseconds = toNumber((value as { nanoseconds?: unknown }).nanoseconds) ?? 0
      const epochMillis = seconds * 1_000 + nanoseconds / 1_000_000
      return toDateFromEpochMillis(epochMillis)
    }
  }

  return null
}

function toOptionalMediaKind(value: unknown): InstagramMediaKind | null {
  const parsed = toNullableString(value)?.toLowerCase()
  if (!parsed || !VALID_MEDIA_KINDS.has(parsed as InstagramMediaKind)) {
    return null
  }
  return parsed as InstagramMediaKind
}

function toOptionalMediaDetectionSource(
  value: unknown,
): InstagramMediaDetectionSource | null {
  const parsed = toNullableString(value)?.toLowerCase()
  if (
    !parsed ||
    !VALID_MEDIA_DETECTION_SOURCES.has(parsed as InstagramMediaDetectionSource)
  ) {
    return null
  }
  return parsed as InstagramMediaDetectionSource
}

interface InferredMediaMetadata {
  mediaKind: InstagramMediaKind
  mediaDetectionSource: InstagramMediaDetectionSource
}

function inferLegacyMediaMetadata(
  permalink: string | null,
  sourceMediaProductType: string | null,
): InferredMediaMetadata {
  const normalizedPermalink = permalink?.toLowerCase() ?? ''
  const normalizedProductType = sourceMediaProductType?.toUpperCase() ?? ''

  if (normalizedProductType === 'REELS') {
    return {
      mediaKind: 'reel',
      mediaDetectionSource: 'product_type',
    }
  }

  if (normalizedPermalink.includes('/reel/')) {
    return {
      mediaKind: 'reel',
      mediaDetectionSource: 'permalink_reel',
    }
  }

  if (normalizedPermalink.includes('/p/')) {
    return {
      mediaKind: 'post',
      mediaDetectionSource: 'permalink_post',
    }
  }

  if (normalizedProductType === 'FEED') {
    return {
      mediaKind: 'post',
      mediaDetectionSource: 'product_type',
    }
  }

  return {
    mediaKind: 'unknown',
    mediaDetectionSource: 'fallback',
  }
}

export function fromFirestoreInstagramReel(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): InstagramReel {
  const data = (docSnap.data() ?? {}) as Partial<FirestoreInstagramReel> &
    Record<string, unknown>
  const reelId = toNullableString(data.reelId) ?? docSnap.id
  const accountId = toNullableString(data.accountId) ?? ''
  const username = toNullableString(data.username) ?? ''
  const caption = typeof data.caption === 'string' ? data.caption : ''
  const permalink = toNullableString(data.permalink) ?? ''
  const thumbnailUrl = toNullableString(data.thumbnailUrl)
  const mediaUrl = toNullableString(data.mediaUrl)
  const mediaType = toNullableString(data.mediaType)
  const mediaProductType = toNullableString(data.mediaProductType)
  const sourceMediaProductType =
    toNullableString(data.sourceMediaProductType) ?? mediaProductType

  const inferredMediaMetadata = inferLegacyMediaMetadata(
    permalink || null,
    sourceMediaProductType,
  )
  const mediaKind = toOptionalMediaKind(data.mediaKind) ?? inferredMediaMetadata.mediaKind
  const mediaDetectionSource =
    toOptionalMediaDetectionSource(data.mediaDetectionSource) ??
    inferredMediaMetadata.mediaDetectionSource

  const timestamp = toOptionalDate(data.timestamp)
  const syncedAt = toOptionalDate(data.syncedAt)
  const updatedAt = toOptionalDate(data.updatedAt)
  const createdAt = toOptionalDate(data.createdAt)

  return {
    uid: docSnap.id,
    reelId,
    accountId,
    username,
    caption,
    permalink,
    thumbnailUrl,
    mediaUrl,
    mediaType,
    mediaProductType,
    mediaKind,
    mediaDetectionSource,
    sourceMediaProductType,
    timestamp,
    timestampMs: timestamp?.getTime() ?? null,
    isActive: toOptionalBoolean(data.isActive, true),
    syncedAt,
    updatedAt,
    createdAt,
    lastSeenSyncRunId: toNullableString(data.lastSeenSyncRunId) ?? undefined,
    displayImageUrl: thumbnailUrl ?? mediaUrl ?? null,
  }
}
